/**
 * Sanitize log output by removing CRLF characters to prevent log injection attacks
 */
export function sanitizeForLog(value: any): string {
    if (typeof value === 'string' || value instanceof String) {
        // Replace carriage returns and line feeds with spaces
        return value.replace(/[\r\n]/g, ' ');
    }
    if (value instanceof Error) {
        return sanitizeForLog(value.message);
    }
    return String(value).replace(/[\r\n]/g, ' ');
}

/**
 * Comprehensive sanitization for security-sensitive log output
 * Prevents log forging attacks by removing all control characters and limiting length
 *
 * This function addresses Veracode CWE-117 (Improper Output Neutralization for Logs)
 * by removing:
 * - CRLF sequences (\r\n) that enable log injection
 * - All ASCII control characters (0-31 and 127) including null bytes, tabs, etc.
 * - Limits output length to prevent log flooding
 *
 * @param value - The untrusted input to sanitize
 * @param maxLength - Maximum length of sanitized output (default: 200)
 * @returns Sanitized string safe for logging
 */
export function sanitizeForSecurityLog(value: any, maxLength = 200): string {
    let str: string;

    // Convert input to string based on type
    if (typeof value === 'string' || value instanceof String) {
        str = String(value);
    } else if (value instanceof Error) {
        str = value.message || 'Error';
    } else if (value === null) {
        return 'null';
    } else if (value === undefined) {
        return 'undefined';
    } else {
        try {
            str = String(value);
        } catch {
            return '[Unable to convert to string]';
        }
    }

    return str
        // Remove all ASCII control characters (0-31 and 127)
        // This includes: null bytes, tabs, line feeds, carriage returns, etc.
        // eslint-disable-next-line no-control-regex -- Intentionally matching control characters for sanitization
        .replace(/[\x00-\x1F\x7F]/g, '')
        // Truncate to prevent log flooding attacks
        .substring(0, maxLength)
        // Trim any remaining whitespace
        .trim();
}

/**
 * Safe console.error wrapper that prevents log forging attacks
 * Use this instead of console.error when logging untrusted data
 * Automatically sanitizes all arguments using sanitizeForSecurityLog
 */
export function safeConsoleError(...args: any[]): void {
    const sanitizedArgs = args.map(arg => sanitizeForSecurityLog(arg));
    console.error(...sanitizedArgs);
}

/**
 * Safe console.warn wrapper that prevents log forging attacks
 * Use this instead of console.warn when logging untrusted data
 * Automatically sanitizes all arguments using sanitizeForSecurityLog
 */
export function safeConsoleWarn(...args: any[]): void {
    const sanitizedArgs = args.map(arg => sanitizeForSecurityLog(arg));
    console.warn(...sanitizedArgs);
}

/**
 * Safe console.log wrapper that prevents log forging attacks
 * Use this instead of console.log when logging untrusted data
 * Automatically sanitizes all arguments using sanitizeForSecurityLog
 */
export function safeConsoleLog(...args: any[]): void {
    const sanitizedArgs = args.map(arg => sanitizeForSecurityLog(arg));
    console.log(...sanitizedArgs);
}
